import { computed, observable, reaction, runInAction } from 'mobx';
import { pathToRegexp } from 'path-to-regexp';
import { GravityFormInfo } from '@teliaee/gravity-forms';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { urlWithLang } from '../util/NavigationUtil';
import LanguageStore from './LanguageStore';
import { RootStore } from './RootStore';
import RouteStore from './RouteStore';
import config from '../Config';
import SsoStore from './SsoStore';

const THIRTY_SECONDS = 30 * 1000;
const TITLE_SUFFIX = 'Telia';

const titleWithSuffix = (title: string): string => (title ? `${title} - ${TITLE_SUFFIX}` : TITLE_SUFFIX);

export interface PageContent {
  id?: number;
  postId?: number;
  date?: string;
  title?: string;
  originalTitle?: string;
  description?: string;
  link?: string;
  linkWithoutLang?: string;
  slug?: string;
  image?: string;
  content?: string;
  noindex?: boolean;
  nofollow?: boolean;
  excerpt?: string;
  feedImageUrl?: string;
  breadcrumbs?: { path: string; anchor: string }[];
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideBreadCrumbs?: boolean;
  whiteBreadCrumbs?: boolean;
  showLoginNotice?: boolean;
  customSegment?: boolean;
  type?: string;
  redirectUrl?: string;
  canonical?: string;
  locales?: string[];
  tags?: string[];
  backgroundColor?: string;
}

export const CONTENT_EDIT_LOCAL_STORAGE_KEY = 'contentEditEnabled';

class ContentStore {
  @observable accessor status: string = 'UNINITIALIZED';
  @observable accessor currentPageContent: PageContent = {};
  @observable accessor currentContentHeight: number = null;
  @observable accessor formsList: GravityFormInfo[];

  private routeStore: RouteStore;
  private languageStore: LanguageStore;
  private ssoStore: SsoStore;

  private regexpFrontPageRedirect = pathToRegexp('/:lang(et|ru|en)');
  private regexp404Redirect = pathToRegexp('/{:lang}?/404');

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
    this.ssoStore = rootStore.ssoStore;
    this.formsList = rootStore.initialState.formsList;

    runInAction(() => {
      const initialContent = rootStore.initialState && rootStore.initialState.initialContent;
      if (initialContent) {
        this.currentPageContent = initialContent;
        this.status = initialContent.id ? 'FETCHED' : 'NOT_FOUND';
      } else if (rootStore.initialState && Object.keys(rootStore.initialState).length > 0 && !rootStore.initialState.isMyTeliaUserAgent) {
        this.status = 'NOT_FOUND';
      }

      if (!rootStore.isNode) {
        reaction(() => this.routeStore.pathname, this.startFetch, {
          fireImmediately: this.status === 'UNINITIALIZED'
        });
      }

      this.setupPostEditFunction();
    });
  }

  private setupPostEditFunction = () => {
    if (!window.telia.toggleCMS) {
      window.telia.toggleCMS = () => {
        if (localStorage.getItem(CONTENT_EDIT_LOCAL_STORAGE_KEY)) {
          localStorage.removeItem(CONTENT_EDIT_LOCAL_STORAGE_KEY);
        } else {
          localStorage.setItem(CONTENT_EDIT_LOCAL_STORAGE_KEY, 'true');
        }
        window.location.reload();
      };
    }
  };

  private handleRedirects = (pathname: string) => {
    if (this.regexp404Redirect.exec(pathname)) {
      this.resetState('NOT_FOUND');
      return;
    }
    if (this.regexpFrontPageRedirect.exec(pathname)) {
      this.routeStore.push(this.ssoStore.customerType === 'business' ? '/ari' : '/era');
    }
  };

  private startFetch = async () => {
    runInAction(() => {
      this.status = 'FETCHING';
    });
    this.handleRedirects(this.routeStore.pathname);
    try {
      const pageContent = (
        await API.get('/content/contentByPath', {
          params: { path: this.routeStore.pathname + this.routeStore.search },
          timeout: THIRTY_SECONDS
        })
      ).data;
      pageContent?.redirectUrl ? this.handleRedirect(pageContent) : this.updateContent(pageContent);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      if (this.isFetching) {
        this.resetState('NOT_FOUND');
        document.querySelector('meta[name="robots"]').setAttribute('content', 'noindex');
      }
    }
  };

  private handleRedirect(pageContent: PageContent) {
    this.routeStore.replace(urlWithLang(pageContent.redirectUrl, this.languageStore.currentLanguage));
  }

  private updateContent = (data: PageContent) => {
    runInAction(() => {
      this.currentPageContent = data;
      this.status = 'FETCHED';
    });
    const defaultOgImage: string = 'https://res.cloudinary.com/telia/image/upload/f_auto,fl_lossy,q_auto:best/OpenGraph/telia-OG-1200x630.png';
    const title = titleWithSuffix(data.title);
    // XXX: store makes assumption that page is rendered. Should be done in PageContent, where is actually rendered
    // Should also reset these attrs when 404
    document.querySelector('title').innerText = title;
    document.querySelector('meta[property="og:title"]').setAttribute('content', title);
    document.querySelector('meta[name="description"]').setAttribute('content', data.description);
    document.querySelector('meta[property="og:description"]').setAttribute('content', data.description);
    document.querySelector('meta[property="og:image"]').setAttribute('content', data.image ? data.image : defaultOgImage);
    document.querySelector('meta[name="robots"]').setAttribute('content', '');
    document.querySelector('link[rel="canonical"]').setAttribute('href', data.canonical || `${config.teliaWebUrl}${data.link}`);
  };

  public setCurrentContentHeight = (height: number) => {
    runInAction(() => {
      this.currentContentHeight = height;
    });
  };

  @computed
  get isCurrentContentAvailableInEnglish(): boolean {
    return this.isFetched && this.currentPageContent.locales && this.currentPageContent.locales.includes('en_US');
  }

  @computed
  get isPageThatShouldUseCookieSegmentForHeader(): boolean {
    return this.isFetched && this.currentPageContent.customSegment;
  }

  @computed
  get notFound() {
    return this.status === 'NOT_FOUND';
  }

  @computed
  get isFetching() {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.status);
  }

  @computed
  get isFetched() {
    return this.status === 'FETCHED';
  }

  private resetState = (status: string) => {
    runInAction(() => {
      this.status = status;
      this.currentPageContent = {};
    });
  };
}

export default ContentStore;
